import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { BrandLogo } from './BrandLogo';
import { ThemeToggle } from './ThemeToggle';
import { UserMenu } from './UserMenu';
import { useI18n } from '../../contexts/I18nContext';
import { useAuth } from '../../contexts/AuthContext';
import { getDefaultAppRoute } from '../../utils/profiles';
import { ArrowRightFromLine, Menu, X } from 'lucide-react';

export function Header({ showUserMenu = true }: { showUserMenu?: boolean }) {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const location = useLocation();

  const [hidden, setHidden] = useState(false);
  const [lastScroll, setLastScroll] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setHidden(y > lastScroll && y > 80);
      setLastScroll(y);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [lastScroll]);

  const goToApp = () => {
    const lang = window.location.pathname.match(/^\/([a-z]{2})/)?.[1] || 'it';
    window.location.href = `/${lang}${getDefaultAppRoute()}`;
  };

  const isLoginPage = typeof window !== 'undefined' && window.location.pathname.includes('/login');
  
  // Determinare se siamo sulla landing page (es: "/", "/it", "/en")
  const isLanding = location.pathname === '/' || location.pathname === `/${lang}` || location.pathname === `/${lang}/`;

  const getNavHref = (hash: string) => {
    return isLanding ? hash : `/${lang}/${hash}`;
  };

  return (
  <div
    className="header-notch-wrapper fixed top-0 z-50 w-full transition-transform duration-300 pointer-events-none"
    style={{ transform: hidden ? 'translateY(-100%)' : 'translateY(0)' }}
  >
  <header
    data-nosnippet
    className="flex items-center justify-between gap-2 px-2 py-[11px] md:px-8 md:py-[16px] backdrop-blur-md border-b border-border-subtle shadow-sm w-full pointer-events-auto"
    style={{ backgroundColor: 'color-mix(in oklab, var(--canvas) 82%, transparent)' }}
  >
  <BrandLogo showVersion={true} className="shrink min-w-0" />
  
  {/* Desktop Navigation */}
  <nav data-nosnippet className="hidden lg:flex items-center gap-1 px-2 py-1.5 bg-surface-elevated dark:bg-zinc-800/50 dark:bg-white/5 border border-border-subtle rounded-full mx-auto">
  <a href={getNavHref('#features-section')} className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 lg:px-4 py-1.5 rounded-full text-text-primary hover:text-text-primary hover:bg-surface-hover transition-all shadow-sm hover:shadow-md">
  {t('landing', 'nav_features')}
  </a>
  <a href={getNavHref('#privacy-section')} className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 lg:px-4 py-1.5 rounded-full text-text-primary hover:text-text-primary hover:bg-surface-hover transition-all shadow-sm hover:shadow-md">
  {t('landing', 'nav_privacy')}
  </a>
  <a href={getNavHref('#testimonials-section')} className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 lg:px-4 py-1.5 rounded-full text-text-primary hover:text-text-primary hover:bg-surface-hover transition-all shadow-sm hover:shadow-md">
  {t('landing', 'nav_testimonials')}
  </a>
  <a href={getNavHref('#pricing')} className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 lg:px-4 py-1.5 rounded-full text-text-primary hover:text-text-primary hover:bg-surface-hover transition-all shadow-sm hover:shadow-md">
  {t('landing', 'nav_pricing')}
  </a>
  <a href={getNavHref('#referral-section')} className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 lg:px-4 py-1.5 rounded-full text-text-primary hover:text-text-primary hover:bg-surface-hover transition-all shadow-sm hover:shadow-md">
  {t('dashboard', 'menu_referral')}
  </a>
  </nav>

  <div className="flex items-center gap-1 sm:gap-2 md:gap-3 flex-shrink-0 min-w-0">
  {user ? (
  <>
  <button
  onClick={goToApp}
  aria-label={t('landing', 'cta_app')}
  title={t('landing', 'cta_app')}
  className="flex items-center gap-2 p-2 lg:px-4 lg:py-2 text-[13px] font-bold rounded-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white shadow-lg shadow-purple-500/20 transition-all cursor-pointer border-none whitespace-nowrap shrink-0"
  >
  <ArrowRightFromLine size={14} />
  <span className="hidden lg:inline">{t('landing', 'cta_app')}</span>
  </button>
  {showUserMenu && <UserMenu />}
  </>
  ) : (
  !isLoginPage && (
<a
  href={`/${lang}/login`}
  aria-label={t('auth', 'login_title')}
  className="flex items-center gap-2 px-3 py-2 sm:px-4 text-[13px] font-bold rounded-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white shadow-lg shadow-purple-500/20 transition-all cursor-pointer border-none whitespace-nowrap no-underline"
  >
  <ArrowRightFromLine size={14} />
  <span className="max-[380px]:hidden">{t('auth', 'login_title')}</span>
  </a>
 )
 )}
  <button
    type="button"
    onClick={() => setMobileOpen(v => !v)}
    aria-label={t('landing', 'nav_menu')}
    aria-expanded={mobileOpen}
    className="lg:hidden flex items-center justify-center w-9 h-9 rounded-full text-text-primary hover:bg-surface-hover transition-colors pointer-events-auto"
  >
    {mobileOpen ? <X size={20} /> : <Menu size={20} />}
  </button>
  <div className="hidden lg:block">
  <ThemeToggle />
  </div>
  </div>
  </header>
  {mobileOpen && (
  <nav data-nosnippet className="lg:hidden pointer-events-auto mx-2 mt-2 rounded-2xl border border-border-subtle bg-surface-card shadow-xl flex flex-col p-2 gap-1">
  <a href={getNavHref('#features-section')} onClick={() => setMobileOpen(false)} className="text-sm font-semibold px-4 py-3 rounded-xl text-text-primary hover:bg-surface-hover transition-colors no-underline">
  {t('landing', 'nav_features')}
  </a>
  <a href={getNavHref('#privacy-section')} onClick={() => setMobileOpen(false)} className="text-sm font-semibold px-4 py-3 rounded-xl text-text-primary hover:bg-surface-hover transition-colors no-underline">
  {t('landing', 'nav_privacy')}
  </a>
  <a href={getNavHref('#testimonials-section')} onClick={() => setMobileOpen(false)} className="text-sm font-semibold px-4 py-3 rounded-xl text-text-primary hover:bg-surface-hover transition-colors no-underline">
  {t('landing', 'nav_testimonials')}
  </a>
  <a href={getNavHref('#pricing')} onClick={() => setMobileOpen(false)} className="text-sm font-semibold px-4 py-3 rounded-xl text-text-primary hover:bg-surface-hover transition-colors no-underline">
  {t('landing', 'nav_pricing')}
  </a>
  <a href={getNavHref('#referral-section')} onClick={() => setMobileOpen(false)} className="text-sm font-semibold px-4 py-3 rounded-xl text-text-primary hover:bg-surface-hover transition-colors no-underline">
  {t('dashboard', 'menu_referral')}
  </a>
  <div className="flex items-center justify-between px-4 py-3 rounded-xl text-text-primary">
  <span className="text-sm font-semibold">{t('dashboard', 'toggle_theme')}</span>
  <ThemeToggle />
  </div>
  </nav>
  )}
  </div>
  );
}

